import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronDown, Download, FileSpreadsheet, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { useNotify } from "@/components/app/page";

/**
 * One «Выгрузить» per page.
 *
 * Whatever on screen can be exported registers itself here -- the page's xlsx
 * report, a tab's table, a grid's CSV -- and the page toolbar lists them in a
 * single menu. A table deep inside a tab therefore does not need a button of
 * its own, and the menu always matches exactly what is mounted.
 */
export type ExportAction = {
  id: string;
  label: string;
  /** What is in the file, in a few words: «за период», «строки таблицы». */
  description?: string;
  format: "xlsx" | "csv";
  run: () => void | Promise<void>;
  disabled?: boolean;
  /** Lower first. Page reports use 0, tables 10. */
  order?: number;
};

type Registry = {
  actions: ExportAction[];
  register: (action: ExportAction) => void;
  unregister: (id: string) => void;
};

const ExportContext = createContext<Registry | null>(null);

export function ExportProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<Map<string, ExportAction>>(() => new Map());

  const register = useCallback((action: ExportAction) => {
    setMap((prev) => new Map(prev).set(action.id, action));
  }, []);
  const unregister = useCallback((id: string) => {
    setMap((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      actions: [...map.values()].sort((a, b) => (a.order ?? 5) - (b.order ?? 5)),
      register,
      unregister,
    }),
    [map, register, unregister],
  );
  return <ExportContext.Provider value={value}>{children}</ExportContext.Provider>;
}

/**
 * Offers an export in the page's menu while the calling component is mounted.
 * `run` is read through a ref, so a closure over fresh rows is used without
 * re-registering on every render; pass `null` to withdraw it.
 */
export function useExport(action: (Omit<ExportAction, "run"> & { run: ExportAction["run"] }) | null) {
  const registry = useContext(ExportContext);
  const runRef = useRef(action?.run);
  runRef.current = action?.run;

  const id = action?.id;
  const label = action?.label;
  const description = action?.description;
  const format = action?.format;
  const disabled = action?.disabled;
  const order = action?.order;

  useEffect(() => {
    if (!registry || !id || !label || !format) return;
    registry.register({ id, label, description, format, disabled, order, run: () => runRef.current?.() });
    return () => registry.unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, label, description, format, disabled, order]);
}

const FormatIcon = ({ format }: { format: ExportAction["format"] }) =>
  format === "xlsx" ? <FileSpreadsheet /> : <FileText />;

/** The toolbar's export control: a button for one export, a menu for several. */
export function ExportMenu() {
  const registry = useContext(ExportContext);
  const notify = useNotify();
  const [busy, setBusy] = useState<string | null>(null);
  const actions = registry?.actions ?? [];

  if (actions.length === 0) return null;

  const run = async (action: ExportAction) => {
    setBusy(action.id);
    try {
      await action.run();
    } catch {
      notify("Не удалось выгрузить", action.label, "destructive");
    } finally {
      setBusy(null);
    }
  };

  const icon = busy ? <Spinner /> : <Download />;

  if (actions.length === 1) {
    const only = actions[0]!;
    return (
      <Button variant="secondary" size="sm" disabled={only.disabled || busy !== null} onClick={() => void run(only)}>
        {icon}
        {busy ? "Готовим…" : "Выгрузить"}
        <Badge size="sm" color="neutral" className="uppercase">
          {only.format}
        </Badge>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" disabled={busy !== null}>
          {icon}
          {busy ? "Готовим…" : "Выгрузить"}
          <ChevronDown className="opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Выгрузить</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {actions.map((action) => (
          <DropdownMenuItem key={action.id} disabled={action.disabled} onSelect={() => void run(action)}>
            <FormatIcon format={action.format} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">{action.label}</span>
              {action.description && (
                <span className="text-muted-foreground text-xs text-pretty">{action.description}</span>
              )}
            </span>
            <Badge size="sm" color="neutral" className="uppercase">
              {action.format}
            </Badge>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
